import { CouchUserInfo, SubscriptionStatus, UserLicenseType } from './data-model/couch-user';
import { isInGrace } from './data-model/license-reconciler';

/**
 * The rules for moving a subscription from the wrong account to the right one
 * (#4248 Phase 6).
 *
 * This is the messiest recurring support case (#4227, #4198, #4207): the
 * customer paid, but the membership landed on a duplicate account — a
 * logged-out checkout, a typo'd email, a second sign-up.
 *
 * It is also **the only irreversible operation in this tooling**, which is why
 * the decision of whether a move is allowed lives here as a PURE function: the
 * refusals can then be exercised exhaustively in unit tests, and the exact same
 * rules run in the dry run and in the apply. A refusal that only fires at apply
 * time would be worthless.
 */

/** Why a repoint was refused. A code as well as prose, so callers can branch. */
export enum RepointRefusalReason {
  /** Nothing to move. */
  SourceHasNoSubscription = 'source-has-no-subscription',
  /** The destination already has a live subscription of its own. Moving onto it
   *  would stack two provider blocks on one account, which the single-provider
   *  policy forbids — and this operation cannot be undone. */
  TargetAlreadySubscribed = 'target-already-subscribed',
  /** Source and destination are the same account. */
  SameAccount = 'same-account',
  /** Apple / Google Play subscriptions are bound to the buyer's STORE account.
   *  We cannot change who owns the purchase at the store, so moving our record
   *  would leave us saying one thing and the store saying another. */
  StoreSubscriptionNotMovable = 'store-subscription-not-movable',
  /** PayPro records the owning uid on its side and we have no supported way to
   *  change it, so a moved PayPro subscription would be dragged back to the old
   *  account by the next IPN. Refused until that write exists. */
  PayProNotRepointable = 'paypro-not-repointable',
  /** More than one live subscription on the source: we will not guess which one
   *  the operator meant to move. */
  SourceHasMultipleLiveSubscriptions = 'source-has-multiple-live-subscriptions',
  /** The SOURCE holds a live coaching subscription (#4362). A coaching
   *  subscription is bound to the coach's client roster, invites and branding,
   *  so the billing block cannot travel on its own — and before this guard the
   *  repoint quietly moved the coach's RETIRED PERSONAL block instead, while
   *  reporting success. */
  CoachSubscriptionNotMovable = 'coach-subscription-not-movable',
}

/** Which `CouchUserInfo` field carries the block being moved. */
export type RepointBlockField = 'paddleData' | 'payProData';

/** The decision, plus everything a caller needs to describe or perform it. */
export interface RepointPlan {
  allowed: boolean;
  /** Set when refused. */
  reason?: RepointRefusalReason;
  /** Plain-language explanation, suitable to show an operator verbatim. */
  explanation?: string;
  /** Set when allowed: which provider is moving. */
  provider?: UserLicenseType;
  /** Set when allowed: which field to move. */
  blockField?: RepointBlockField;
}

/** The web rails, the only ones a repoint can move. Keyed to the field name so
 *  the caller does not have to re-derive it. */
const MOVABLE_RAILS: Array<{ provider: UserLicenseType; field: RepointBlockField }> = [
  { provider: UserLicenseType.Paddle, field: 'paddleData' },
  { provider: UserLicenseType.PayPro, field: 'payProData' },
];

/**
 * Decide whether this subscription may be moved, and which block moves.
 *
 * Pure: it reads both user records and returns a decision, touching nothing.
 * The caller performs the move only when `allowed` is true.
 */
export function planSubscriptionRepoint(
  from: CouchUserInfo,
  to: CouchUserInfo,
  now: number = Date.now(),
): RepointPlan {
  // Treat a missing name as "cannot tell them apart" rather than as "different".
  // Falling through here on a self-repoint would write the block and then
  // immediately clear it, losing the subscription altogether.
  if (!from?.name || !to?.name || from.name === to.name) {
    return refuse(RepointRefusalReason.SameAccount,
      'The source and destination must be two different, identifiable accounts.');
  }

  // A store subscription cannot be moved, and this is checked BEFORE "is there
  // anything to move": telling an operator "no subscription found" when the
  // customer plainly has an App Store subscription would send them hunting for
  // the wrong problem.
  const storeRail = findLiveStoreRail(from, now);
  if (storeRail) {
    return refuse(RepointRefusalReason.StoreSubscriptionNotMovable,
      `This subscription was bought through ${storeRail === UserLicenseType.Apple ? 'Apple' : 'Google Play'}, `
      + 'and store subscriptions belong to the buyer\'s own store account. We cannot move who owns it at the store, '
      + 'so moving only our record would leave our system and the store disagreeing. '
      + 'The customer needs to cancel there and re-subscribe on the right account.');
  }

  // A COACH's own coaching subscription is not movable, and this is checked
  // BEFORE the movable-rail selection below for the same reason the store check
  // above carries: refuse for the TRUE reason before a later branch can pick the
  // wrong block. Without this guard, a coach fell straight through to their
  // RETIRED PERSONAL Paddle block — the one becoming a coach leaves behind — and
  // the repoint moved that instead, reporting success while the real coaching
  // subscription stayed on the source account.
  //
  // It tests `coachSeatData` and `coachTierData` BY NAME, and deliberately NOT
  // `coachData`, even though `findAnyLiveRail` below checks all three. The two
  // questions are different ones. `coachData` is the CLIENT-side mirror of
  // whichever block backs somebody's coach, and every coached client carries
  // one — so including it here would refuse a repoint for any coached client,
  // blocking the duplicate-account repair this module exists for, irreversibly.
  // See `design-docs/coach-subscription-lapse.md`.
  if (isBlockLive(from?.coachSeatData, now) || isBlockLive(from?.coachTierData, now)) {
    return refuse(RepointRefusalReason.CoachSubscriptionNotMovable,
      'This account holds a COACHING subscription, which cannot be moved to another account. '
      + 'A coaching subscription is attached to the coach\'s client roster, their pending invites and their '
      + 'branding, so moving the billing block on its own would leave a coach with no clients and their clients '
      + 'with no coach. If the coach is on the wrong account, sort the coaching side out first.');
  }

  // Prefer a LIVE block. Picking the first rail that merely HAS an id would move
  // a dead one and leave the paying subscription behind on the wrong account —
  // and report success. A retired-but-recorded block is a normal state (the
  // coach handoff leaves one behind deliberately), so this is not hypothetical.
  const liveRails = MOVABLE_RAILS.filter(rail => isBlockLive(from?.[rail.field], now));
  if (liveRails.length > 1) {
    return refuse(RepointRefusalReason.SourceHasMultipleLiveSubscriptions,
      'The source account has more than one live web subscription, and moving the wrong one cannot be undone. '
      + 'Sort the source account out first — cancel or re-sync the one that should not be there — then repoint.');
  }
  const movable = liveRails[0] ?? MOVABLE_RAILS.find(rail => hasSubscriptionId(from, rail.field));
  if (!movable) {
    return refuse(RepointRefusalReason.SourceHasNoSubscription,
      'The source account has no web subscription to move.');
  }

  // PayPro records the owning uid on ITS side too, and we have no supported call
  // to change it. Moving only our record would look like it worked and then be
  // undone by the next IPN, which is the worst possible outcome for an operation
  // advertised as irreversible. Refuse until that write exists.
  if (movable.provider === UserLicenseType.PayPro) {
    return refuse(RepointRefusalReason.PayProNotRepointable,
      'PayPro subscriptions cannot be moved yet. PayPro records which account owns the subscription on its own side, '
      + 'and we have no supported way to change that — so the move would appear to work and then be undone the next '
      + 'time PayPro sends us an update. Handle this one by cancelling and re-subscribing on the right account.');
  }

  // The destination must not already have a LIVE subscription of its own. A
  // lapsed or terminated block is fine to move onto — refusing there would block
  // the ordinary "they re-signed-up later" case for no benefit.
  //
  // The one exception is a COPY of the very subscription being moved (#4207).
  // The #4198 provisioning race gave one customer two or three accounts, and
  // every one of them then received the same webhooks, so each ended up holding
  // its own copy of the same subscription. A copy is not a second subscription,
  // and refusing it blocked the exact repair this module was written for.
  const targetLive = findAnyLiveRail(to, now);
  if (targetLive !== undefined && !isCopyOfSameSubscription(from, to, movable.field, now)) {
    // Two very different situations reach this refusal, and telling an operator
    // the wrong one sends them to the wrong repair. The destination may hold a
    // genuinely SECOND subscription — or it may hold a copy of this very one
    // that is FRESHER than the source, where the danger is overwriting the
    // authoritative copy with a stale one.
    const destinationHoldsACopy = sameSubscriptionIdOnBothSides(from, to, movable.field);
    return refuse(RepointRefusalReason.TargetAlreadySubscribed,
      destinationHoldsACopy
        ? 'The destination account already holds a copy of this same subscription, and its copy was updated '
          + 'MORE recently than the one being moved (or one of them records no update time at all). Moving the '
          + 'staler copy on top would overwrite what the payment provider last told us and could cost this '
          + 'customer their access, so it is refused. Re-sync both accounts, then repoint.'
        : `The destination account already has a live ${targetLive} subscription. `
          + 'Moving another one onto it would leave the account holding two subscriptions at once, which is not allowed '
          + 'and cannot be undone. Sort out the destination account first — cancel or re-sync it — then repoint.');
  }

  return { allowed: true, provider: movable.provider, blockField: movable.field };
}

function refuse(reason: RepointRefusalReason, explanation: string): RepointPlan {
  return { allowed: false, reason, explanation };
}

/** Whether both accounts record the SAME provider-side subscription id on this
 *  block — i.e. the destination holds a copy of the very subscription being
 *  moved, rather than a second one of its own. Used only to pick which refusal
 *  to explain. */
function sameSubscriptionIdOnBothSides(
  from: CouchUserInfo,
  to: CouchUserInfo,
  field: RepointBlockField,
): boolean {
  const fromId = (from?.[field] as { subscriptionId?: string } | undefined)?.subscriptionId;
  const toId = (to?.[field] as { subscriptionId?: string } | undefined)?.subscriptionId;
  return !!fromId && fromId === toId;
}

/** An ISO timestamp as an instant, or undefined when it is missing or
 *  unparseable. Deliberately not a string comparison — see the caller. */
function toMillisOrUndefined(iso: string | undefined): number | undefined {
  if (!iso) {
    return undefined;
  }
  const millis = Date.parse(iso);
  return Number.isNaN(millis) ? undefined : millis;
}

/**
 * Whether the destination's live subscription is simply a COPY of the one being
 * moved, rather than a second subscription of its own (#4207).
 *
 * Three conditions, and ALL are needed:
 *
 *  - the two accounts record the SAME provider-side subscription id on the same
 *    block. An id is what proves they are one subscription; if either side has
 *    no id recorded, nothing proves it and we refuse rather than guess.
 *  - the destination has NO OTHER live rail besides that block. This is the part
 *    that stops the exception widening into a hole: a coach with a live seat, or
 *    an account with a redeemed key, is carrying a genuinely separate
 *    subscription that moving another one on top would stack. It is checked by
 *    hiding the copy and asking the ordinary question again, so it keeps covering
 *    every rail `findAnyLiveRail` knows about and cannot drift from that list.
 *  - the copy being MOVED is at least as recently validated as the one it
 *    overwrites. The move assigns the source block over the destination's, so
 *    the question that matters is which copy is AUTHORITATIVE — not which one
 *    is live.
 *
 *    Getting this backwards is easy, so it is worth stating what the real case
 *    looks like. In #4227 the source is the copy the provider actually talks to
 *    and it reads `Terminated`, because it received the cancellation; the
 *    destination is the customer's own account and it still reads `Active`,
 *    because it never heard. A "the source must be live" rule would refuse
 *    exactly that repair, and overwriting a stale `Active` with a true
 *    `Terminated` is the whole point — the customer really did cancel.
 *
 *    What must NOT happen is the mirror image: a stale copy overwriting the
 *    fresh one, which would cost a paying customer their access. `lastValidated_utc`
 *    separates the two, because it records the last webhook that wrote the
 *    block. If either side does not carry one, nothing proves which is
 *    authoritative and the move is refused.
 */
function isCopyOfSameSubscription(
  from: CouchUserInfo,
  to: CouchUserInfo,
  field: RepointBlockField,
  now: number,
): boolean {
  const fromBlock = from?.[field] as { lastValidated_utc?: string } | undefined;
  const toBlock = to?.[field] as { lastValidated_utc?: string } | undefined;
  // Compared as INSTANTS, not as strings. Every Paddle writer stamps a `…Z`
  // form today, and two of those sort correctly as text — but an offset form
  // (`…+02:00`) sorts by its wall-clock digits against a `…Z` sibling, so a
  // genuinely older copy could compare as newer. That is the precise outcome
  // this rule exists to prevent, so it does not rest on every writer keeping
  // the same spelling forever.
  const fromValidated = toMillisOrUndefined(fromBlock?.lastValidated_utc);
  const toValidated = toMillisOrUndefined(toBlock?.lastValidated_utc);
  if (fromValidated === undefined || toValidated === undefined || fromValidated < toValidated) {
    return false;
  }
  const fromId = (from?.[field] as { subscriptionId?: string } | undefined)?.subscriptionId;
  const toId = (to?.[field] as { subscriptionId?: string } | undefined)?.subscriptionId;
  if (!fromId || !toId || fromId !== toId) {
    return false;
  }
  const destinationWithoutTheCopy = { ...to, [field]: undefined } as CouchUserInfo;
  return findAnyLiveRail(destinationWithoutTheCopy, now) === undefined;
}

/** True when the user has a provider-side id on that block — i.e. there is a
 *  real subscription recorded, live or not. A lapsed subscription is still worth
 *  moving: the customer's payment history and their access record go with it. */
function hasSubscriptionId(user: CouchUserInfo, field: RepointBlockField): boolean {
  const block = user?.[field] as { subscriptionId?: string } | undefined;
  return !!block?.subscriptionId;
}

/** The store rail this user currently has ACCESS through, if any. */
function findLiveStoreRail(user: CouchUserInfo, now: number): UserLicenseType | undefined {
  if (isBlockLive(user?.appleData, now)) return UserLicenseType.Apple;
  if (isBlockLive(user?.googlePlayData, now)) return UserLicenseType.GooglePlay;
  return undefined;
}

/**
 * Any rail this user currently has a live subscription through.
 *
 * It checks EVERY block the reconciler can pick a winner from, not just the four
 * obvious ones. The dangerous omission is the coach blocks: moving a personal
 * Paddle subscription onto a coach who holds a live `coachSeatData` would leave
 * two live Paddle subscriptions on one account and double-bill them — the
 * invariant (V6 / V10 in `design-docs/coach-subscription-coordination.md`) that
 * the cross-provider gate exists to protect everywhere else.
 */
function findAnyLiveRail(user: CouchUserInfo, now: number): UserLicenseType | undefined {
  if (isBlockLive(user?.paddleData, now)) return UserLicenseType.Paddle;
  if (isBlockLive(user?.payProData, now)) return UserLicenseType.PayPro;
  if (isBlockLive(user?.coachSeatData, now)) return UserLicenseType.CoachSeat;
  if (isBlockLive(user?.coachTierData, now)) return UserLicenseType.CoachTier;
  if (isBlockLive(user?.coachData, now)) return UserLicenseType.Coach;
  if (isBlockLive(user?.keyData, now)) return UserLicenseType.Key;
  return findLiveStoreRail(user, now);
}

/**
 * Whether a provider block is a live subscription for repoint purposes.
 *
 * This is deliberately WIDER than the reconciler's `isInGrace`. That allow-list
 * answers "does this grant access right now", and correctly excludes `Paused` —
 * a paused subscriber has no access. But a paused subscription is dormant and
 * still RESUMABLE at the provider, so for "may I overwrite this block?" it very
 * much counts: overwriting it would destroy the `subscriptionId` of a
 * subscription the customer can still restart, leaving us unable to cancel it
 * and with no record it ever existed. The sibling coach gate takes exactly the
 * same view. `Waiting` counts too — that is a checkout in flight.
 *
 * Exported because the same question is asked outside repointing. The officer
 * permissions screen must not write a never-expiring hand grant on top of a
 * live rail: the grant would win the reconciler and the app would stop
 * offering that rail's cancel, manage and resume controls. That is the same
 * "may I displace this block?" question, so it must have the same answer.
 */
export function isBlockLive(
  block: { subscriptionStatus?: SubscriptionStatus; expiresDate_utc?: string } | undefined,
  now: number,
): boolean {
  if (!block) return false;
  const status = block.subscriptionStatus ?? SubscriptionStatus.None;
  if (status === SubscriptionStatus.Paused || status === SubscriptionStatus.Waiting) {
    return true;
  }
  return isInGrace(status, block.expiresDate_utc, now);
}
